import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, FormsModule } from '@angular/forms';
import { OnChangeFn, OnTouchedFn, provideCva } from '../cva.utils';

export type TextareaAppearance = 'outline' | 'underline' | 'floating';

@Component({
  selector: 'app-textarea',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './textarea.component.html',
  styleUrls: ['./textarea.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideCva(TextareaComponent)],
  host: {
    '[class.app-textarea-focused]': 'focused()',
    '[class.app-textarea-disabled]': 'disabled()',
    '[class.app-textarea-has-value]': 'hasValue()',
    '[class.app-textarea-error]': 'hasError()',
    '[class.app-textarea-underline]': 'appearance() === "underline"',
    '[class.app-textarea-floating]': 'isFloating()',
    '[class.app-textarea-floated]': 'isFloated()',
  },
})
export class TextareaComponent implements ControlValueAccessor {
  readonly label = input<string>('');
  readonly hint = input<string>('');
  readonly placeholder = input<string>('');
  readonly appearance = input<TextareaAppearance>('outline');
  readonly inputId = input<string>('');
  readonly helperText = input<string>('');
  readonly errorMessage = input<string>('');
  readonly required = input<boolean>(false);
  readonly rows = input<number>(3);
  readonly maxLength = input<number | null>(null);
  readonly showCount = input<boolean>(false);

  readonly blurred = output<void>();

  readonly value = signal('');
  readonly disabled = signal(false);
  readonly focused = signal(false);
  readonly touched = signal(false);

  readonly isFloating = computed(() => this.appearance() === 'floating');

  readonly isFloated = computed(
    () => this.isFloating() && (this.focused() || this.hasValue()),
  );

  readonly hasValue = computed(() => {
    const v = this.value();
    return v !== '' && v !== null && v !== undefined;
  });

  readonly hasError = computed(() => !!this.errorMessage());

  readonly displayMessage = computed(
    () => this.errorMessage() || this.helperText(),
  );

  readonly characterCount = computed(() => this.value()?.length ?? 0);

  readonly countHint = computed(() => {
    if (!this.showCount() || this.maxLength() == null) {
      return '';
    }
    return `${this.characterCount()}/${this.maxLength()}`;
  });

  readonly effectiveHint = computed(() => this.hint() || this.countHint());

  readonly effectivePlaceholder = computed(() => {
    if (this.isFloating() && this.label() && !this.isFloated()) {
      return '';
    }
    return this.placeholder();
  });

  readonly showStaticLabelRow = computed(() => {
    if (this.isFloating()) {
      return !!this.effectiveHint();
    }
    return !!(this.label() || this.effectiveHint());
  });

  readonly textareaRef = viewChild<ElementRef<HTMLTextAreaElement>>('textareaEl');

  private onChange: OnChangeFn<string> = () => {};
  private onTouched: OnTouchedFn = () => {};

  writeValue(value: string | null | undefined): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: OnChangeFn<string>): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: OnTouchedFn): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  onModelChange(raw: string): void {
    this.value.set(raw ?? '');
    this.onChange(this.value());
  }

  onFocus(): void {
    this.focused.set(true);
  }

  onBlur(): void {
    this.focused.set(false);
    if (!this.touched()) {
      this.touched.set(true);
      this.onTouched();
    }
    this.blurred.emit();
  }

  focus(): void {
    this.textareaRef()?.nativeElement.focus();
  }
}
